import { useActiveParticipantsViewCreator } from '@viewCreators/active-participants-view-creator';
import React, { useEffect } from 'react';
import { ParticipantView } from '@views';
import { useUpdateAccountabilityPartnershipsAction } from '@actions/Admin/UpdateAccountabilityPartnershipsAction';
import { AccountabilityPartnership } from '@stores/AccountabilityPartnerStore';

type AccountabilityPartnerPairingEditorController = {
  selectedPartners: { participantId: number; partnerId: number }[];
  setSelectedPartners: (selectedPartners: { participantId: number; partnerId: number }[]) => void;
  clearAllPartnerships: () => void;
  savePartnerships: () => void;
  data: Map<ParticipantView, ParticipantView[]>;
  isLoading: boolean;
  clearPartners: boolean;
  clearPartnerships: (clearPartners: boolean) => void;
};
export const useAccountabilityPartnerPairingEditorController = (
  groupId: number
): AccountabilityPartnerPairingEditorController => {
  const { data: activeParticipants, isLoading } = useActiveParticipantsViewCreator(groupId);
  const [data, setData] = React.useState<Map<ParticipantView, ParticipantView[]>>(new Map());
  const { mutate: updateAction } = useUpdateAccountabilityPartnershipsAction();

  const updatePartnerships = (partnerships: AccountabilityPartnership[]) => {
    updateAction({ groupId, partnerships });
  };

  const savePartnerships = () => {
    const pairings = selectedPartners.filter((x) => x.partnerId !== -1);
    updatePartnerships(pairings);
    setSelectedPartners(buildDefaultPartners());
  };

  const [selectedPartners, setSelectedPartners] = React.useState<{ participantId: number; partnerId: number }[]>([]);

  function buildDefaultPartners() {
    return data.entries().reduce(
      (acc, [participant]) => {
        acc.push({ participantId: participant.id, partnerId: -1 });
        return acc;
      },
      [] as { participantId: number; partnerId: number }[]
    );
  }

  useEffect(() => {
    if (!data) return;
    const partners = buildDefaultPartners();
    setSelectedPartners(partners);
  }, [data]);

  const [clearPartners, setClearPartners] = React.useState(false);
  const clearAllPartnerships = () => {
    setClearPartners(true);

    //flip this back to false after setting to true
    setTimeout(() => {
      setClearPartners(false);
    }, 100); // or a small delay like 100ms if needed

    if (!data) return;
    const defaultPartners = buildDefaultPartners();
    setSelectedPartners(defaultPartners);
  };

  useEffect(() => {
    const map = new Map<ParticipantView, ParticipantView[]>();
    if (!activeParticipants) return;
    activeParticipants.forEach((participant) => {
      map.set(
        participant,
        activeParticipants.filter((p) => p.id !== participant.id)
      );
    });
    setData(map);
  }, [activeParticipants]);

  return {
    clearPartners,
    clearPartnerships: clearAllPartnerships,
    selectedPartners,
    setSelectedPartners,
    clearAllPartnerships,
    savePartnerships,
    data,
    isLoading,
  } as AccountabilityPartnerPairingEditorController;
};
